import axios, { AxiosRequestConfig, AxiosResponse, AxiosError } from 'axios';
import {
  useQuery,
  useMutation,
  useQueryClient,
  UseQueryOptions,
  UseMutationOptions,
  QueryKey,
} from '@tanstack/react-query';
import { config } from './config';

interface HttpClientConfig extends AxiosRequestConfig {
  baseURL: string;
  responseType: 'json' | 'blob';
  timeout: number;
  headers: {
    'X-Requested-With': string;
    'Content-Type': string;
    Authorization: string;
  };
}

const client = axios.create({
  baseURL: config.API_BASE_URL,
  responseType: 'json',
  timeout: 20000,
  headers: {
    'X-Requested-With': 'XMLHttpRequest',
    'Content-Type': 'application/json',
    Authorization: `Bearer ${localStorage.getItem('token')}`,
  },
} as HttpClientConfig);

export const formClient = axios.create({
  baseURL: config.API_BASE_URL,
  responseType: 'json',
  timeout: 10000,
  headers: {
    'X-Requested-With': 'XMLHttpRequest',
    'Content-Type': 'multipart/form-data',
    Authorization: `Bearer ${localStorage.getItem('token')}`,
  },
} as HttpClientConfig);

export const documentClient = axios.create({
  baseURL: config.API_BASE_URL,
  responseType: 'blob',
  timeout: 10000,
  headers: {
    'X-Requested-With': 'XMLHttpRequest',
    'Content-Type': 'application/json',
    Authorization: `Bearer ${localStorage.getItem('token')}`,
  },
} as HttpClientConfig);

export const request = async <T = any>(
  options: AxiosRequestConfig
): Promise<T> => {
  const onSuccess = (response: AxiosResponse<T>): T => {
    console.debug('Request Successful!', response);
    return response.data;
  };

  const onError = (error: AxiosError): never => {
    console.error('Request Failed:', error.toJSON());
    if (error.response?.status === 401) {
      window.location.href = '/login';
      let email = localStorage.getItem('email');
      localStorage.clear();
      if (email) {
        localStorage.setItem('email', email);
      }
    }
    throw error.response?.data || error.message;
  };

  return client({ ...options })
    .then(onSuccess)
    .catch(onError);
};

// Generic type for API functions
type ApiFunction<TParams = any, TResponse = any> = (
  params?: TParams
) => Promise<TResponse>;

// Generic type for success callback
type SuccessCallback<TData = any> = (data: TData) => void;

// Generic type for error callback
type ErrorCallback<TError = any> = (error: TError) => void;

export const useApiGet = <TData = any, TParams = any>(
  key: QueryKey,
  fn: ApiFunction<TParams, TData>,
  options?: Omit<
    UseQueryOptions<TData, Error, TData, QueryKey>,
    'queryKey' | 'queryFn'
  >,
  stringParams?: TParams
) => {
  return useQuery<TData, Error, TData, QueryKey>({
    queryKey: key,
    queryFn: stringParams ? () => fn(stringParams) : () => fn(),
    ...options,
  });
};

export const useApiSend = <TData = any, TParams = any>(
  fn: ApiFunction<TParams, TData>,
  success?: SuccessCallback<TData>,
  error?: ErrorCallback<Error>,
  invalidateKey?: QueryKey[],
  options?: Omit<
    UseMutationOptions<TData, Error, TParams, unknown>,
    'mutationFn' | 'onSuccess' | 'onError'
  >
) => {
  const queryClient = useQueryClient();

  return useMutation<TData, Error, TParams>({
    mutationFn: fn,
    onSuccess: (data: TData) => {
      if (invalidateKey) {
        invalidateKey.forEach((key) => {
          queryClient.invalidateQueries({ queryKey: key });
        });
      }
      if (success) {
        success(data);
      }
    },
    onError: error,
    retry: 0,
    ...options,
  });
};

// Additional utility types for common use cases
export interface ApiResponse<T = any> {
  data: T;
  message?: string;
  success: boolean;
  error?: string;
}

export interface PaginatedResponse<T = any> {
  data: T[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface ApiError {
  message: string;
  status: number;
  code?: string;
}

// Type-safe request wrapper with generic response type
export const typedRequest = async <T = any>(
  options: AxiosRequestConfig
): Promise<T> => {
  return request<T>(options);
};

// Type-safe GET request helper
export const get = async <T = any>(
  url: string,
  config?: AxiosRequestConfig
): Promise<T> => {
  return request<T>({ ...config, method: 'GET', url });
};

// Type-safe POST request helper
export const post = async <T = any>(
  url: string,
  data?: any,
  config?: AxiosRequestConfig
): Promise<T> => {
  return request<T>({ ...config, method: 'POST', url, data });
};

// Type-safe PUT request helper
export const put = async <T = any>(
  url: string,
  data?: any,
  config?: AxiosRequestConfig
): Promise<T> => {
  return request<T>({ ...config, method: 'PUT', url, data });
};

// Type-safe DELETE request helper
export const del = async <T = any>(
  url: string,
  config?: AxiosRequestConfig
): Promise<T> => {
  return request<T>({ ...config, method: 'DELETE', url });
};

// Type-safe PATCH request helper
export const patch = async <T = any>(
  url: string,
  data?: any,
  config?: AxiosRequestConfig
): Promise<T> => {
  return request<T>({ ...config, method: 'PATCH', url, data });
};
